// Liten render-helper för auto-genererade city×hub-sidor.
// Tar emot resultat från cityHubCopy och returnerar färdig route-config
// (head + component) som varje route-fil bara behöver spridit på.

import { LandingPage } from "@/components/site/landing/LandingPage";
import { LocalMobileGallery } from "@/components/site/landing/LocalMobileGallery";
import { galleryPhotos } from "@/content/photo-gallery";
import {
  breadcrumbs,
  faqPage,
  serviceSchema,
  localBusinessForCity, aeoGeo } from "@/lib/seo/schema";

import type { CityHubPage } from "@/lib/seo/cityHubCopy";
import type { CityMeta } from "@/lib/seo/cities";
import { primaryPathFor } from "@/lib/seo/canonical-overrides";

/**
 * FEN-011 (kannibalisering): ortssidor med samma sökintention som en primär
 * mall pekar canonical mot den primära sidan. Sidorna finns kvar för besökare
 * och internlänkning men slutar tävla i index.
 */
const CANONICAL_HUB_MAP: Record<string, string> = {
  videoproduktion: "filmproduktion",
  produktionsbolag: "filmproduktion",
  videobyra: "filmbyra",
};

export function canonicalCityPath(path: string): string {
  const [, hub, slug] = path.split("/");
  const target = hub ? CANONICAL_HUB_MAP[hub] : undefined;
  const hubMapped = target && slug ? `/${target}/${slug}` : path;
  return primaryPathFor(hubMapped);
}

export function cityHubRoute(city: CityMeta, page: CityHubPage) {
  const { meta, faq, props } = page;
  const canonicalPath = canonicalCityPath(meta.path);
  const absUrl = `https://fenny.se${canonicalPath}`;
  const localImages = galleryPhotos.slice(0, 8);
  const ogImage = localImages[0]?.src?.startsWith("/")
    ? `https://fenny.se${localImages[0].src}`
    : undefined;
  return {
    head: () => ({
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { property: "og:title", content: meta.ogTitle },
        { property: "og:description", content: meta.ogDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absUrl },
        { name: "geo.region", content: "SE" },
        { name: "geo.placename", content: city.name },
        { name: "twitter:card", content: "summary_large_image" },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { name: "twitter:image", content: ogImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: canonicalPath, name: meta.title, description: meta.description })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            localBusinessForCity({
              city: city.name,
              path: meta.path,
              description: meta.schemaName,
              postalRegion: city.region,
              bookingPath: "/kontakt",
              images: localImages,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: `${meta.breadcrumbLabel}`,
              path: canonicalPath,
              serviceType: meta.breadcrumbHubLabel,
              description: meta.description,
              areaServed: [city.name, city.region, ...city.neighbors],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqPage(faq)),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: meta.breadcrumbHubLabel, path: meta.breadcrumbHubPath },
              { name: meta.breadcrumbLabel, path: meta.path },
            ]),
          ),
        },
      ],
    }),
    component: () => (
      <LandingPage
        mobileCollapseAfter={2}
        {...props}
        afterHero={
          <>
            <LocalMobileGallery
              photos={galleryPhotos.slice(0, 6)}
              label={`Film & foto · ${city.name}`}
              ctaTo="/kontakt"
              ctaLabel="Boka 30 min – kostnadsfritt"
              heading="Ur produktionerna"
            />
            {props.afterHero ? <div className="hidden md:block">{props.afterHero}</div> : null}
          </>
        }
      />
    ),
  };
}
